import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { FAMILY_CARD_FONT_FAMILY } from '../family-card-form/familyCardFont';
import { useFitFontSize, VALUE_INK_SHADOW } from '../shared/formFieldEmphasis';
import { buildEmbassyBirthForm } from './embassyBirthFormEngine';
import {
    BASELINE_RATIO,
    LEADER,
    LINE_BOX,
    PAGE,
    PAGE_HEIGHT,
    PAGE_WIDTH,
    PITCH,
    RULE,
    TYPE,
    VALUE_LIFT,
    VALUE_TYPE,
    X_SHIFT,
    cellFit,
} from './embassyBirthMetrics';

export const EMBASSY_BIRTH_PAGE_WIDTH = PAGE_WIDTH;
export const EMBASSY_BIRTH_PAGE_HEIGHT = PAGE_HEIGHT;

const LEADER_TEXT = LEADER.char.repeat(LEADER.count);

const PAGE_STYLE = {
    position: 'relative',
    width: PAGE_WIDTH,
    height: PAGE_HEIGHT,
    background: '#fff',
    color: '#111',
    boxSizing: 'border-box',
    fontFamily: FAMILY_CARD_FONT_FAMILY,
    fontSize: TYPE.body.size,
    overflow: 'hidden',
};

/** A measured baseline becomes the CSS top of the line box. */
const topFor = (baseline, size) => baseline - size * BASELINE_RATIO;

const textStyle = (size, weight) => ({
    position: 'absolute',
    fontSize: size,
    fontWeight: weight,
    lineHeight: LINE_BOX,
    whiteSpace: 'pre',
    fontFamily: 'inherit',
});

/**
 * Chrome rounds a face's ascent and descent to whole pixels when it lays out a
 * line, so where a baseline lands inside the line box drifts with the font
 * size — up to ~0.9 pt off what `BASELINE_RATIO` says at the sizes the answers
 * fit to. Measure where this span's baseline really is and take up the
 * difference in `margin-top`. The export resets that margin: html2canvas places
 * text by its own rule, which `generateEmbassyBirthPdf` corrects for.
 */
function useBaselineSnap(ref, deps) {
    useLayoutEffect(() => {
        const snap = () => {
            const el = ref.current;
            if (!el) return;
            el.style.marginTop = '0px';
            const size = parseFloat(el.style.fontSize);
            if (!size || !el.getClientRects().length) return;
            const marker = document.createElement('i');
            marker.style.cssText = 'display:inline-block;width:0;height:0;vertical-align:baseline';
            el.appendChild(marker);
            // Rects carry the preview's scale; the computed height does not
            // (offsetHeight would, but rounded to a whole pixel).
            const box = el.getBoundingClientRect();
            const scale = box.height / parseFloat(getComputedStyle(el).height) || 1;
            const baseline = (marker.getBoundingClientRect().top - box.top) / scale;
            marker.remove();
            el.style.marginTop = `${size * BASELINE_RATIO - baseline}px`;
        };
        snap();
        // Measured against a fallback face the snap is wrong, and `fonts.ready`
        // may already have settled before Kalpurush was even requested — so wait
        // for Kalpurush itself at this size.
        let cancelled = false;
        const size = parseFloat(ref.current?.style.fontSize);
        if (size && document.fonts) {
            document.fonts.load(`${size}px Kalpurush`).then(() => {
                if (!cancelled) snap();
            }).catch(() => {});
        }
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}

/** One printed line, anchored by baseline and by left edge or centre. */
function Text({ item }) {
    const ref = useRef(null);
    useBaselineSnap(ref, [item.t, item.size]);
    const size = item.size ?? TYPE.body.size;
    const style = { ...textStyle(size, item.weight ?? TYPE.body.weight), top: topFor(item.y, size) };
    if (item.center != null) {
        style.left = item.center + X_SHIFT;
        style.transform = 'translateX(-50%)';
    } else {
        style.left = item.x + X_SHIFT;
    }
    return <span ref={ref} style={style}>{item.t}</span>;
}

/** A dotted leader: real full stops, clipped to the span the form gives it. */
function Leader({ item }) {
    const size = TYPE.body.size;
    return (
        <span
            aria-hidden
            data-leader
            data-leader-pitch={PITCH}
            style={{
                ...textStyle(size, TYPE.body.weight),
                whiteSpace: 'nowrap',
                top: topFor(item.y, size),
                left: item.from + X_SHIFT,
                width: Math.max(0, item.to - item.from),
                overflow: 'hidden',
                letterSpacing: LEADER.letterSpacing(size, PITCH),
            }}
        >
            {LEADER_TEXT}
        </span>
    );
}

/** Where an answer's baseline goes: centred in its cell, or on (and lifted off) its dots. */
const valueBaseline = (item, size) => (item.row ? cellFit(item.row, size).baseline : item.y - (item.lift ?? VALUE_LIFT));

const valueStyle = (size, item) => ({
    ...textStyle(size, VALUE_TYPE.weight),
    whiteSpace: 'nowrap',
    top: topFor(valueBaseline(item, size), size),
    textShadow: VALUE_INK_SHADOW,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

/** The largest an answer may print: the value face, capped by its cell's height. */
const maxValueSize = (item) => {
    const size = item.size ?? VALUE_TYPE.size;
    return item.row ? cellFit(item.row, size).size : size;
};

/**
 * A typed answer: larger than the form's own text, and shrinking toward
 * `minSize` only when the cell is too short for it. Cell answers in the
 * centred columns (dates, parents) are centred the way their labels are.
 */
function Value({ item, text }) {
    const inset = item.inset ?? (item.align === 'center' ? 1 : 0);
    const width = Math.max(0, item.to - item.from - inset * 2);
    const maxSize = maxValueSize(item);
    const [ref, size] = useFitFontSize(
        maxSize,
        Math.min(maxSize, item.minSize ?? VALUE_TYPE.minSize),
        [text, width],
    );
    useBaselineSnap(ref, [text, size]);
    if (!text) return null;
    const style = { ...valueStyle(size, item), left: item.from + inset + X_SHIFT };
    if (item.align === 'center') {
        style.width = width;
        style.textAlign = 'center';
    } else {
        style.maxWidth = width;
    }
    return (
        <span ref={ref} data-field-value data-filled="true" style={style}>
            {text}
        </span>
    );
}

let measureCanvas = null;
function measureWidth(text, size) {
    if (typeof document === 'undefined') return text.length * size * 0.5;
    if (!measureCanvas) measureCanvas = document.createElement('canvas');
    const ctx = measureCanvas.getContext('2d');
    ctx.font = `${VALUE_TYPE.weight} ${size}px ${FAMILY_CARD_FONT_FAMILY}`;
    return ctx.measureText(text).width;
}

/**
 * Break `text` at spaces into at most `widths.length` lines, at the largest
 * size from `size` down to `minSize` where it all fits. Whatever still does not
 * fit at `minSize` stays on the last line and is clipped there.
 */
function wrapToLines(text, widths, size, minSize) {
    const words = text.split(/\s+/).filter(Boolean);
    let s = size;
    for (;;) {
        const lines = [];
        let i = 0;
        for (let line = 0; line < widths.length && i < words.length; line += 1) {
            let current = words[i];
            i += 1;
            while (i < words.length && measureWidth(`${current} ${words[i]}`, s) <= widths[line]) {
                current = `${current} ${words[i]}`;
                i += 1;
            }
            lines.push(current);
        }
        const fits = i >= words.length && lines.every((l, n) => measureWidth(l, s) <= widths[n]);
        if (fits || s <= minSize) {
            if (i < words.length) lines[lines.length - 1] = [lines[lines.length - 1], ...words.slice(i)].join(' ');
            return { lines, size: s };
        }
        s = Math.max(minSize, s - 0.3);
    }
}

/**
 * An answer the form gives two ruled lines for (the addresses). It is split
 * into one absolutely placed line per rule rather than left to wrap, so each
 * line keeps its measured baseline in the preview and in the export alike.
 */
function WrapValue({ item, text }) {
    const widths = item.lines.map((line) => line.to - line.from);
    const size = maxValueSize({ ...item, row: item.lines[0].row });
    const minSize = Math.min(size, item.minSize ?? VALUE_TYPE.minSize);
    const [fit, setFit] = useState(() => (text ? wrapToLines(text, widths, size, minSize) : null));

    useLayoutEffect(() => {
        if (!text) { setFit(null); return undefined; }
        setFit(wrapToLines(text, widths, size, minSize));
        // Kalpurush loads asynchronously; measure again once it is in.
        let cancelled = false;
        document.fonts?.ready.then(() => {
            if (!cancelled) setFit(wrapToLines(text, widths, size, minSize));
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, widths.join(','), size, minSize]);

    if (!text || !fit) return null;
    return fit.lines.map((line, n) => (
        // eslint-disable-next-line react/no-array-index-key
        <WrapLine key={n} slot={item.lines[n]} size={fit.size} text={line} />
    ));
}

function WrapLine({ slot, size, text }) {
    const ref = useRef(null);
    useBaselineSnap(ref, [text, size]);
    return (
        <span
            ref={ref}
            data-field-value
            data-filled="true"
            style={{
                ...valueStyle(size, { lift: 0, ...slot }),
                left: slot.from + X_SHIFT,
                maxWidth: slot.to - slot.from,
            }}
        >
            {text}
        </span>
    );
}

/** A table rule, drawn at the scan's stroke weight. */
function Rule({ left, top, width, height }) {
    return (
        <span
            aria-hidden
            style={{ position: 'absolute', left: left + X_SHIFT, top, width, height, background: RULE.color }}
        />
    );
}

function HLine({ item }) {
    const stroke = item.stroke ?? RULE.stroke;
    return <Rule left={item.x0 - RULE.stroke / 2} top={item.y - stroke / 2} width={item.x1 - item.x0 + RULE.stroke} height={stroke} />;
}

function VLine({ item }) {
    return <Rule left={item.x - RULE.stroke / 2} top={item.y0 - RULE.stroke / 2} width={RULE.stroke} height={item.y1 - item.y0 + RULE.stroke} />;
}

/**
 * Everything drawn here is built from filled spans, like the rules: html2canvas
 * drops inline SVG and sub-pixel borders from this export, but paints a
 * background — rotated or not — every time.
 */
function Bar({ left, top, width, height, rotate, color = RULE.color, radius }) {
    return (
        <span
            aria-hidden
            style={{
                position: 'absolute',
                left: left + X_SHIFT,
                top,
                width,
                height,
                background: color,
                borderRadius: radius,
                transform: rotate ? `rotate(${rotate}deg)` : undefined,
                transformOrigin: '0 50%',
            }}
        />
    );
}

/**
 * The printed tick box — a light grey square on the scan; ticked, it carries a
 * check. A 0.5 pt grey stroke reads the same in the browser and in the 2×
 * export raster; a thinner, darker one rendered heavy on screen and faint on paper.
 */
function Tick({ item, ticked }) {
    const s = 0.5;
    const c = '#888';
    return (
        <>
            <Bar left={item.x} top={item.y} width={item.w} height={s} color={c} />
            <Bar left={item.x} top={item.y + item.h - s} width={item.w} height={s} color={c} />
            <Bar left={item.x} top={item.y} width={s} height={item.h} color={c} />
            <Bar left={item.x + item.w - s} top={item.y} width={s} height={item.h} color={c} />
            {ticked ? (
                <span data-field-value data-filled="true">
                    {/* A short stroke down to the bottom of the box, then a long one up past its corner. */}
                    <Bar left={item.x + 0.6} top={item.y + item.h * 0.5} width={2.7} height={1.15} rotate={50} color="#111" radius={0.6} />
                    <Bar left={item.x + 2.1} top={item.y + item.h * 0.95} width={6.8} height={1.15} rotate={-60} color="#111" radius={0.6} />
                </span>
            ) : null}
        </>
    );
}

/** The cut line: the scan's hyphens, 1.6 pt long at a 2.26 pt pitch. */
function Dash({ item }) {
    const count = Math.floor((item.x1 - item.x0 - 1.6) / PITCH) + 1;
    return Array.from({ length: count }, (_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Bar key={i} left={item.x0 + i * PITCH} top={item.y - 0.35} width={1.6} height={0.7} color="#333" />
    ));
}

/** ✂ drawn rather than typed: Kalpurush has no glyph for it. Handles left, blades right. */
function Scissors({ item }) {
    const ring = (top) => (
        <span
            aria-hidden
            style={{
                position: 'absolute',
                left: item.x + 0.4 + X_SHIFT,
                top: item.y + top,
                width: 2.6,
                height: 2,
                borderRadius: '50%',
                border: '0.75px solid #222',
                boxSizing: 'border-box',
            }}
        />
    );
    return (
        <>
            {ring(0.25)}
            {ring(4.95)}
            <Bar left={item.x + 2.7} top={item.y + 1.8} width={7.4} height={0.75} rotate={38} />
            <Bar left={item.x + 2.7} top={item.y + 5.4} width={7.4} height={0.75} rotate={-38} />
        </>
    );
}

function Item({ item, d }) {
    switch (item.k) {
        case 'text': return <Text item={item} />;
        case 'leader': return <Leader item={item} />;
        case 'value': return <Value item={item} text={d[item.field]} />;
        case 'wrap': return <WrapValue item={item} text={d[item.field]} />;
        case 'hline': return <HLine item={item} />;
        case 'vline': return <VLine item={item} />;
        case 'tick': return <Tick item={item} ticked={d[item.field] === item.when} />;
        case 'dash': return <Dash item={item} />;
        case 'scissors': return <Scissors item={item} />;
        default: return null;
    }
}

function EmbassyBirthFormPreview({ form }, ref) {
    const d = buildEmbassyBirthForm(form);

    return (
        <div ref={ref} data-embassy-birth-document>
            <div data-embassy-birth-page style={PAGE_STYLE}>
                {PAGE.map((item, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Item key={`${item.k}-${index}`} item={item} d={d} />
                ))}
            </div>
        </div>
    );
}

export default forwardRef(EmbassyBirthFormPreview);
